import { gql, useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { GetApp as GetAppIcon } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import React from "react";
import { useOpenAttachment } from "../../attachments/useOpenAttachment.js";
import { useOpenFile } from "../../../commons/hooks/useOpenFile.js";
import { Attachment } from "../../../../clients/graphqlTypes.js";
import {
  GeneratePurchaseOrderPdfDocument,
  GeneratePurchaseOrderPdfQuery,
  GeneratePurchaseOrderPdfQueryVariables,
} from "./PurchaseOrderDownloadButton.generated.js";
import { useTranslate } from "@tolgee/react";

interface Props {
  size?: "small" | "medium";
  projectId: string;
  orderId: string;
  orderFile?: Pick<Attachment, "id" | "url"> | null;
}

export const PurchaseOrderDownloadButton = ({
  size = "small",
  projectId,
  orderId,
  orderFile,
}: Props) => {
  const { t } = useTranslate("PurchaseOrders");
  const { openPdf } = useOpenFile();
  const { openAttachment } = useOpenAttachment();

  const client = useApolloClient();

  const onClick = () => {
    if (orderFile?.url) {
      openAttachment(
        { kind: "order_pdf", projectId, ownerId: orderId },
        `PurchaseOrder_${orderId}`,
        { as: "pdf" }
      );
      return;
    }
    // No stored file: generate one. openPdf opens the tab before awaiting
    // the generation and reports a failure itself.
    openPdf(async () => {
      const { data } = await client.query<
        GeneratePurchaseOrderPdfQuery,
        GeneratePurchaseOrderPdfQueryVariables
      >({ query: GeneratePurchaseOrderPdfDocument, variables: { orderId } });
      const url = getDataOrNull(data.orderGeneratePdf)?.url;
      if (!url) throw new Error("Failed to generate PDF");
      return url;
    }, `PurchaseOrder_${orderId}`);
  };
  return (
    <Tooltip title={t("Download PDF")}>
      <IconButton size={size} color="primary" onClick={onClick}>
        <GetAppIcon />
      </IconButton>
    </Tooltip>
  );
};
